// Capability-document uploads on the shared resumable uploader: the transport of the /capability-documents/uploads routes (the server answers a known sha256 with the stored document, so no separate lookup is needed; a completed upload returns the document, its reading job or why reading has not started) and this browser's paused capability uploads in their own IndexedDB database, so they never mix with Datasets › Files.
import {
  capabilitiesCompleteCapabilityUpload,
  capabilitiesCreateCapabilityUpload,
  capabilitiesDeleteCapabilityUpload,
  capabilitiesGetCapabilityUpload,
  type CapabilityDocumentDetail,
  type CapabilityUploadSession,
  type Problem,
} from "@/api/__generated__";
import { browserRecordStore } from "@/lib/upload/store";
import { isGoneUpload, partSender } from "@/lib/upload/transport";
import type { UploadSessionOf, UploadTransport } from "@/lib/upload/uploader";

export interface StoredCapabilityDocument {
  sha256: string;
  document: CapabilityDocumentDetail;
  jobId: string | null;
  readingProblem: Problem | null;
}

export const CAPABILITY_UPLOADS_PATH = "/api/v1/capability-documents/uploads";

export function storedDocument(
  document: CapabilityDocumentDetail,
  jobId: string | null = document.job_id,
  readingProblem: Problem | null = null,
): StoredCapabilityDocument {
  return { sha256: document.capability_doc_uid, document, jobId, readingProblem };
}

function sessionOf(session: CapabilityUploadSession): UploadSessionOf<StoredCapabilityDocument> {
  const { capability_document: known, ...rest } = session;
  return { ...rest, deduplicated_source: known === null ? null : storedDocument(known) };
}

export const capabilityUploadTransport: UploadTransport<StoredCapabilityDocument> = {
  findSourceFile: () => Promise.resolve(null),
  async createUpload(body) {
    const { data } = await capabilitiesCreateCapabilityUpload({ body, throwOnError: true });
    return sessionOf(data);
  },
  async getUpload(uploadId) {
    try {
      const { data } = await capabilitiesGetCapabilityUpload({
        path: { upload_id: uploadId },
        throwOnError: true,
      });
      return sessionOf(data);
    } catch (problem) {
      if (isGoneUpload(problem)) {
        return null;
      }
      throw problem;
    }
  },
  putPart: partSender(CAPABILITY_UPLOADS_PATH),
  async completeUpload(uploadId) {
    const { data } = await capabilitiesCompleteCapabilityUpload({
      path: { upload_id: uploadId },
      throwOnError: true,
    });
    return {
      source_file: storedDocument(data.capability_document, data.job_id, data.reading_problem),
      deduplicated: data.deduplicated,
      profile_job_id: data.job_id,
    };
  },
  async deleteUpload(uploadId) {
    await capabilitiesDeleteCapabilityUpload({ path: { upload_id: uploadId }, throwOnError: true });
  },
};

export const capabilityUploadStore = browserRecordStore("gridcode-capability-uploads");
